/**
 * THE RE-CONFIRMATION QUEUE — shape, wire coercion, date handling, wording.
 *
 * DEPENDENCY-FREE, for the same reason counters-format.ts is: workers/decay.ts
 * takes a D1Database, so importing its types into the dashboard would drag
 * Workers runtime types into the Pages tsc graph and break the build. That has
 * now happened enough times (capture→sentinel, facts→volatility,
 * counters→counters-format) that it is a pattern, not an accident: anything both
 * lanes need lives in its own file with no runtime imports.
 *
 * WHAT IS IN HERE THAT LOOKS LIKE UI BUT IS NOT. The certificate-date
 * conversion below is the reason this module carries more than types. A date
 * input hands back "2027-03-15" and the engine wants epoch seconds, and the
 * obvious `new Date(value).getTime() / 1000` is wrong in a way nobody notices:
 * an ISO date-only string parses as UTC MIDNIGHT, so a certificate the owner
 * says runs "to 15 March" is recorded as expiring at the START of the 15th and
 * the assistant begins hedging a live insurance policy a day early. One
 * conversion, in one tested place, used by every caller.
 */

/**
 * What the owner is being asked to do. 'renew' is cert_tied only — see
 * workers/decay.ts for why a certificate must never be offered a Confirm.
 */
export type DueAction = 'confirm' | 'renew'

/** One fact awaiting the owner, as the queue and the heartbeat read it. */
export interface DueFact {
  id: string
  /** The fact itself — nobody can judge "is this still true?" without it. */
  content: string
  volatilityClass: string
  /** Unix epoch seconds; already in the past for everything the queue returns. */
  expiresAt: number
  /** When the owner last stood behind it. Null for rows approved before
   *  last_confirmed was being set. */
  lastConfirmed: number | null
  action: DueAction
  /** Whole days past due, floored. */
  daysOverdue: number
}

/* ---------------------------------------------------------------------------
 * Wire coercion — never trust the wire, and never invent a row.
 * ------------------------------------------------------------------------- */

function asNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

/**
 * Coerce the engine's JSON into DueFact rows.
 *
 * A row without an id or without content is DROPPED, not repaired. Both are the
 * things the owner acts on and reads; a row missing either would render as an
 * empty card with a Confirm button under it, and a Confirm button over nothing
 * is an invitation to attest to something he cannot see. Dropping is the only
 * safe repair.
 *
 * An unrecognised action falls back to 'renew'. That is the deliberately
 * INCONVENIENT default: 'confirm' would hand a one-tap attestation to a row
 * whose type we failed to read, and if that row were certificate-tied the tap
 * would resolve nothing and chase him again next week. Renew asks for a date —
 * more work, but never a false resolution.
 */
export function coerceDueFacts(raw: unknown): DueFact[] {
  if (!Array.isArray(raw)) return []
  const out: DueFact[] = []
  for (const item of raw) {
    const r = (item ?? {}) as Record<string, unknown>
    if (typeof r.id !== 'string' || !r.id) continue
    if (typeof r.content !== 'string' || !r.content) continue
    out.push({
      id: r.id,
      content: r.content,
      volatilityClass: typeof r.volatilityClass === 'string' ? r.volatilityClass : 'unknown',
      expiresAt: asNumber(r.expiresAt),
      lastConfirmed:
        typeof r.lastConfirmed === 'number' && Number.isFinite(r.lastConfirmed)
          ? r.lastConfirmed
          : null,
      action: r.action === 'confirm' ? 'confirm' : 'renew',
      daysOverdue: Math.max(0, Math.floor(asNumber(r.daysOverdue))),
    })
  }
  return out
}

/* ---------------------------------------------------------------------------
 * Certificate dates.
 * ------------------------------------------------------------------------- */

/** Matches an `<input type="date">` value and nothing else. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * "2027-03-15" -> the epoch second at the END of that day, UTC. Null if the
 * string is not a real calendar date.
 *
 * END OF DAY, NOT MIDNIGHT. A certificate that "runs to 15 March" is valid ON
 * the 15th. Recording midnight would expire it a day early and start the
 * assistant hedging a policy that is still live — the engine understating the
 * owner's own cover, in his customers' hearing.
 *
 * The round-trip check is what rejects 2027-02-31: Date.UTC rolls that forward
 * to 3 March rather than failing, so the only way to know the input was real is
 * to build the date and read the parts back.
 */
export function certDateToEpoch(value: string): number | null {
  const m = ISO_DATE.exec(value.trim())
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  const ms = Date.UTC(year, month - 1, day, 23, 59, 59)
  const d = new Date(ms)
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null
  }
  return Math.floor(ms / 1000)
}

/** The inverse, for pre-filling the date input with the expiry already on file. */
export function epochToCertDate(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  return new Date(seconds * 1000).toISOString().slice(0, 10)
}

/* ---------------------------------------------------------------------------
 * Wording.
 * ------------------------------------------------------------------------- */

/**
 * How overdue a fact is, in words. Days, never a timestamp: the owner is
 * deciding whether to deal with this now, and "9 days" answers that where
 * "expired 2026-08-21T09:00:00Z" does not.
 */
export function describeOverdue(daysOverdue: number): string {
  const n = Math.max(0, Math.floor(daysOverdue))
  if (n === 0) return 'Out of date today'
  if (n === 1) return '1 day out of date'
  return `${n} days out of date`
}

/**
 * The queue's own heading. Counting is the whole message — an owner opening the
 * dashboard needs to know whether this is a two-minute job or a sit-down.
 */
export function describeQueue(count: number): string {
  if (count === 0) return 'Nothing needs confirming'
  if (count === 1) return '1 thing needs confirming'
  return `${count} things need confirming`
}

/**
 * Why a certificate-tied row asks for a date instead of offering a Confirm.
 * Stated to the owner rather than left implicit: an interface that silently
 * withholds the easy button from one row and not another reads as a bug.
 */
export const RENEW_EXPLANATION =
  'This one is tied to a certificate, so confirming it would not change when it runs out. Put in the date on the new certificate instead.'
